import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SectionCard } from "../components/SectionCard";
import { useAuth } from "../context/AuthContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { api, notify } from "../lib/api";

interface AvailablePlayer {
  id: string;
  pseudo: string;
}

export function TournamentTeamPage() {
  const { tournamentId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setTrail } = useBreadcrumbs();

  const [teamName, setTeamName] = useState("");
  const [teammatePseudo, setTeammatePseudo] = useState("");
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>(
    [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setTrail([]);
    void api.getTournament(tournamentId).then((t) => {
      if (!active) return;
      setTrail([
        { label: "Tournois", to: "/tournaments" },
        { label: t.name, to: `/tournaments/${tournamentId}` },
        { label: "Équipe" },
      ]);
    });
    return () => {
      active = false;
    };
  }, [tournamentId, setTrail]);

  useEffect(() => {
    void api.listAvailablePlayers(tournamentId).then((players) => {
      // Exclude the current user from the list
      setAvailablePlayers(players.filter((p) => p.pseudo !== user?.pseudo));
    });
  }, [tournamentId, user?.pseudo]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.createTournamentTeam(tournamentId, {
        teamName,
        teammatePseudo,
      });
      notify("success", "Équipe créée");
      navigate(`/tournaments/${tournamentId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg">
      <SectionCard
        title="Créer une équipe"
        subtitle="Créer une équipe de 2 joueurs"
      >
        <form className="space-y-5" onSubmit={(e) => void handleSubmit(e)}>
          <div>
            <label className="label" htmlFor="team-name">
              Nom d'équipe
            </label>
            <input
              id="team-name"
              className="text-field"
              placeholder="Les Rois du Valet"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="label" htmlFor="player-1">
              Joueur 1
            </label>
            <input
              id="player-1"
              className="text-field"
              value={user?.pseudo ?? ""}
              disabled
            />
          </div>

          <div>
            <label className="label" htmlFor="player-2">
              Joueur 2
            </label>
            <select
              id="player-2"
              className="text-field"
              value={teammatePseudo}
              onChange={(e) => setTeammatePseudo(e.target.value)}
              required
            >
              <option value="">Choisir un partenaire</option>
              {availablePlayers.map((p) => (
                <option key={p.id} value={p.pseudo}>
                  {p.pseudo}
                </option>
              ))}
            </select>
            {availablePlayers.length === 0 && (
              <p className="mt-1 text-xs text-smoke">
                Aucun joueur disponible. Tous les joueurs ont déjà une équipe.
              </p>
            )}
          </div>

          {error && (
            <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </p>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !teammatePseudo}
            >
              {submitting ? "Création..." : "Créer l'équipe"}
            </button>
          </div>
        </form>
      </SectionCard>
    </div>
  );
}
